import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import clsx from "classnames";

// material-ui
import makeStyles from "@material-ui/styles/makeStyles";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import Divider from "@material-ui/core/Divider";
import Drawer from "@material-ui/core/Drawer";

import Alert from "@material-ui/lab/Alert";

//icons
// import DownloadIcon from "mdi-material-ui/Download";

import IconButton from "@material-ui/core/IconButton";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";

// jaza-soft
import { WithReduxForm, ReferenceInput, AutoCompleteInput, SelectInput, Button, showNotification } from "jazasoft";

// local
import CardHeader from "../../../../components/CardHeader";
import MuiTable from "../../../../components/MuiTable";

import useMutation from "../../../../hooks/useMutation";

import { useDeepMemo, useEntityPrivilege } from "../../../../hooks";
import { getDistinctValues, isEmpty, toFixed, toFixedValue } from "../../../../utils/helpers";
import { colorTomatoRed } from "../../../../utils/colors";
import hasPrivilege from "../../../../utils/hasPrivilege";
import { ResourceId, Scope } from "../../../../utils/privilege";
import { InventoryStatus } from "../../../../utils/types";

const useStyles = makeStyles(theme => ({
  root: {
    width: 680,
    display: "flex",
    flexDirection: "column",
    height: "100vh"
  },
  drawerPaper: {
    maxWidth: 1000
  },
  drawerHeader: {
    display: "flex",
    width: 680,
    alignItems: "center",
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: "flex-start"
  },
  content: {
    display: "flex",
    flex: 1,
    flexDirection: "column"
  },
  footer: {
    display: "flex",
    padding: "1.5em"
  },
  padding: {
    padding: "1.5em"
  },
  marginVertical: {
    marginTop: "1.5em"
  }
}));

const ItemTransferDrawer = props => {
  const classes = useStyles();
  const { open, fabricColor, rollList, ledger, onDrawerClose, roles, hasAccess, onRefresh } = props;

  const { disableDelete } = useEntityPrivilege("MaterialTransfer");
  const formValues = useSelector(state => state.form["record-form"]?.values);
  const toFabric = useSelector(state =>
    isEmpty(formValues?.toFabricId)
      ? null
      : state.jazasoft?.resources?.fabrics?.data && state.jazasoft?.resources?.fabrics?.data[formValues.toFabricId]
  );

  // const [download, { loading: downloading }] = useMutation({ rawResponse: true });
  const [mutate] = useMutation();
  const dispatch = useDispatch();

  const columns = useDeepMemo(() => {
    if (isEmpty(rollList)) return [];
    let cols = [
      { dataKey: "rollNo", title: "Factory Roll No" },
      { dataKey: "supplierRollNo", title: "Supplier Roll No" },
      { dataKey: "length", title: "Length", render: value => (isEmpty(value) ? "" : `${toFixedValue(value, 2)} m`) },
      { dataKey: "weight", title: "Weight", render: value => (isEmpty(value) ? "" : `${toFixedValue(value, 3)} kg`) },
      { dataKey: "invStatus", title: "Inv. Status" },
      { dataKey: "qcStatus", title: "QC Status" }
    ];
    if (!isEmpty(ledger)) {
      cols = cols.filter(c => !["qcStatus"].includes(c.dataKey));
    }
    if (rollList.every(e => isEmpty(e.weight) || e.weight === 0.0)) {
      cols = cols.filter(c => c.dataKey !== "weight");
    }
    return cols;
  }, [rollList, ledger]);

  // const onDownload = () => {
  //   const filename = `FABRIC_RELEASE`.replaceAll(" ", "-").replaceAll("/", "-");
  //   const columnList = columns.map((c, idx) => ({ ...c, colNo: idx + 1 }));
  //   const rowList = rows.map((row, idx) => ({
  //     rowNo: idx + 1,
  //     cellList: columnList.map(c => ({
  //       dataKey: c.dataKey,
  //       value: row[c.dataKey]
  //     }))
  //   }));
  //   const options = {
  //     url: `export/excel`,
  //     method: "post",
  //     responseType: "arraybuffer",
  //     params: { filename },
  //     data: [{ sheetNo: 0, sheetName: "Marker", autoSizeColumn: true, excelData: { columnList, rowList } }]
  //   };
  //   download(options, {
  //     onSuccess: response => {
  //       if (response.status === 200) {
  //         const header = response.headers["content-disposition"];
  //         var fileName = header ? header.match(/filename="(.+)"/)[1] : `${filename}.xlsx`;
  //         let blob = new Blob([response.data], {
  //           type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  //         });
  //         let link = document.createElement("a");
  //         link.href = window.URL.createObjectURL(blob);
  //         link.download = fileName;
  //         link.click();
  //       }
  //     }
  //   });
  // };

  const errorMessage = useDeepMemo(() => {
    if (isEmpty(rollList)) return "";
    let message;

    //** Correct Selection **//
    // 1. All rolls must belong to single fabric Item
    // 2. All rolls must belong to same current warehouse
    const fabItemIds = getDistinctValues(rollList.map(ri => ri.fabricItem?.id).filter(id => !isEmpty(id)));
    const whList = getDistinctValues(rollList.map(ri => ri.currWarehouse).filter(wh => !isEmpty(wh)));

    if (fabItemIds?.length > 1) {
      message = `Rolls from multiple fabric item cannot be transferred in one transaction.`;
    } else if (whList?.length > 1) {
      message = `All rolls must be selected from one warehouse. Hint: Current warehouse of all selected rolls must be same.`;
    }
    return message;
  }, [rollList]);

  const onTransfer = () => {
    if (!isEmpty(errorMessage) || isEmpty(rollList) || isEmpty(formValues?.toFabricId) || isEmpty(formValues.toEffColor)) return;

    const fabricItem = rollList[0]?.fabricItem || {};

    const toFabricColor = toFabric?.colorList?.find(e => e.effColor === formValues.toEffColor);

    const data = {
      invoiceId: fabricItem.invoice?.id,
      grn: fabricItem.grn,
      itemCode: fabricItem.fabric?.itemCode,
      itemColor: fabricItem.color,
      itemColorCode: fabricItem.colorCode,
      itemColorShade: fabricItem.colorShade,
      fabricPo: fabricItem.fabricPo,
      width: fabricItem.orderingWidth,
      uomWidth: fabricItem.uomWidth,

      fromItemCode: fabricColor?.fabric?.itemCode,
      fromItemColor: fabricColor?.color,
      fromItemColorCode: fabricColor?.code,
      toItemCode: toFabric?.itemCode,
      toItemColor: toFabricColor?.color,
      toItemColorCode: toFabricColor?.code,

      rollList: rollList.map(ri => ({ supplierRollNo: ri.supplierRollNo, factoryRollNo: ri.rollNo, length: ri.length }))
    };

    const options = { url: `material-transfer/item`, method: "post", data };

    mutate(options, {
      onSuccess: () => {
        dispatch(showNotification("Material transferred successfully."));
        onRefresh && onRefresh();
        onDrawerClose && onDrawerClose();
      }
    });
  };

  const onDelete = () => {
    if (isEmpty(ledger)) return;

    const options = { url: `material-transfer/item`, method: "delete", params: { txn: ledger.id } };

    mutate(options, {
      onSuccess: () => {
        dispatch(showNotification("Material transfer transaction deleted successfully."));
        onRefresh && onRefresh();
        onDrawerClose && onDrawerClose();
      }
    });
  };

  const rows = useDeepMemo(() => {
    if (isEmpty(rollList) || rollList?.length <= 1) return rollList;
    return [
      ...rollList,
      {
        rollNo: "Total",
        length: toFixed(
          rollList.reduce((acc, r) => acc + r.length, 0),
          4
        )
      }
    ];
  }, [rollList, ledger]);

  const toColorChoices = toFabric?.colorList?.map(e => ({ id: e.effColor, name: e.effColor })) || [];

  const disabled = rows?.some(ri => ri.invStatus === InventoryStatus.ALLOCATED);

  return (
    <div>
      {/** Drawer For Supplier Rolls Upload Against Fabric Item */}
      <Drawer anchor="right" classes={{ paper: classes.drawerPaper }} open={open} onClose={onDrawerClose}>
        <WithReduxForm>
          {() => (
            <div className={classes.root}>
              <div className={classes.drawerHeader}>
                <IconButton onClick={onDrawerClose}>
                  <ChevronRightIcon />
                </IconButton>
                <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", width: "100%", paddingRight: "1.5em" }}>
                  <Typography variant="h5">Item Transfer {!isEmpty(ledger) ? `: TXN = ${ledger.id}` : ""}</Typography>
                  {/* {!isEmpty(ledger) && (
              <Button label="Download" showLabel variant="outlined" color="primary" disabled={downloading} onClick={onDownload}>
                <DownloadIcon />
              </Button>
            )} */}
                </div>
              </div>
              <Divider />

              <div className={clsx(classes.content, { [classes.padding]: isEmpty(ledger) })}>
                {!isEmpty(errorMessage) && (
                  <div>
                    <Alert severity="error" variant="filled">
                      <Typography variant="h6">{errorMessage}</Typography>
                    </Alert>
                  </div>
                )}

                {isEmpty(ledger) && (
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <ReferenceInput
                        source="toFabricId"
                        label="To Item Code"
                        reference="fabrics"
                        resource="fabrics"
                        searchKeys={["itemCode", "itemDesc", "articleName"]}
                        fullWidth={true}
                        options={{ fullWidth: true }}>
                        <AutoCompleteInput optionText="itemCode" disablePortal={true} />
                      </ReferenceInput>
                    </Grid>
                    <Grid item xs={6}>
                      <SelectInput source="toEffColor" label="To Item Color" choices={toColorChoices} fullWidth />
                    </Grid>
                  </Grid>
                )}

                <div className={clsx({ [classes.marginVertical]: isEmpty(ledger) })} style={{ zIndex: -1 }}>
                  <MuiTable
                    title={<CardHeader title={"Rolls"} style={{ paddingLeft: 0 }} />}
                    columns={columns}
                    rows={rows}
                    searchable={!isEmpty(ledger)}
                    searchKeys={["rollNo"]}
                    toolbar
                    cellStyle={({ row }) => ({ fontWeight: row.rollNo === "Total" ? "bold" : "normal" })}
                  />
                </div>
              </div>

              <div className={classes.footer}>
                {isEmpty(ledger) && (
                  <Button
                    label="Submit"
                    variant="contained"
                    color="primary"
                    disabled={!isEmpty(errorMessage) || isEmpty(formValues?.toFabricId) || isEmpty(formValues.toEffColor)}
                    onClick={onTransfer}
                  />
                )}
                {!isEmpty(ledger) && hasPrivilege(roles, hasAccess, ResourceId.MATERIAL_TRANSFER, Scope.DELETE) && !disableDelete && !disabled && (
                  <Button
                    label="Delete"
                    variant="outlined"
                    style={{ color: colorTomatoRed.dark, borderColor: colorTomatoRed.dark }}
                    onClick={onDelete}
                  />
                )}
              </div>
            </div>
          )}
        </WithReduxForm>
      </Drawer>
    </div>
  );
};

export default ItemTransferDrawer;
